// client/src/pages/clientPortal/PortalSiteSearchPage.js — what the client sees.
//
// The same table the staff screen shows, minus the internal machinery: no sweep
// controls, no release switches, no rejected finds. Only properties a licensee
// has released, and the zoning answer that explains why each one is on the list.
import React, { useState, useEffect } from 'react';
import { useClientPortalApiClient } from '../../hooks/useClientPortalApiClient';

const USE = {
    by_right: ['Permitted outright', 'bg-green-100 text-green-800'],
    special_use: ['Needs village approval', 'bg-amber-100 text-amber-800'],
    not_allowed: ['Not permitted', 'bg-red-100 text-red-800'],
    unverified: ['Zoning being confirmed', 'bg-gray-100 text-gray-700'],
};

const PortalSiteSearchPage = () => {
    const apiClient = useClientPortalApiClient();
    const [searches, setSearches] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        apiClient.get('/api/client-portal/site-searches')
            .then(setSearches)
            .catch((err) => setError(err.message || 'Failed to load your property search.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    if (isLoading) return <p className="text-slate-500">Loading...</p>;
    if (error) return <p className="text-red-500">{error}</p>;

    if (!searches.length) {
        return (
            <div>
                <h1 className="mb-6 text-2xl font-bold text-slate-800">Your Property Search</h1>
                <div className="rounded-lg bg-white p-8 text-slate-500 shadow">
                    Nothing to show yet. Your broker will share properties here as they are found.
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <h1 className="text-2xl font-bold text-slate-800">Your Property Search</h1>

            {searches.map((s) => {
                const crit = s.criteria || {};
                return (
                    <div key={s.search_id} className="space-y-4">
                        <div className="rounded-lg bg-white p-6 shadow">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-800">{s.title}</h2>
                                    <p className="text-sm text-slate-500">
                                        {s.use_category} · to {s.intent}
                                        {(crit.towns || []).length ? ` · ${crit.towns.join(', ')}` : ''}
                                        {crit.min_sf || crit.max_sf ? ` · ${crit.min_sf || ''}–${crit.max_sf || ''} sq ft` : ''}
                                    </p>
                                </div>
                                <span className={`rounded-full px-3 py-1 text-sm font-semibold ${
                                    s.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-700'}`}>
                                    {s.status === 'active' ? 'Searching' : `Closed — ${s.closed_reason || 'ended'}`}
                                </span>
                            </div>
                        </div>

                        {!!(s.jurisdictions || []).length && (
                            <div className="grid gap-3 md:grid-cols-2">
                                {s.jurisdictions.map((j) => (
                                    <div key={`${j.municipality}-${j.use_category}`} className="rounded-lg bg-white p-4 shadow">
                                        <p className="font-semibold text-slate-800">
                                            {j.municipality}
                                            {j.districts_by_right && <span className="ml-1 text-sm font-normal text-slate-500">({j.districts_by_right})</span>}
                                        </p>
                                        <p className="text-sm text-slate-600">{j.verdict}</p>
                                        {j.conditions && <p className="text-sm text-slate-500">Conditions: {j.conditions}</p>}
                                        {(j.official_name || j.official_phone || j.department_phone) && (
                                            <p className="mt-1 text-xs text-slate-500">
                                                Village contact: {j.official_name ? `${j.official_name}${j.official_title ? `, ${j.official_title}` : ''} · ` : ''}
                                                {j.official_phone || j.department_phone}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {s.candidates.length === 0 ? (
                            <div className="rounded-lg bg-white p-6 text-slate-500 shadow">
                                Your broker has not shared any properties for this search yet.
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-lg bg-white shadow">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                                        <tr>
                                            <th className="px-4 py-3 text-left">Address</th>
                                            <th className="px-4 py-3 text-left">Size</th>
                                            <th className="px-4 py-3 text-left">Rate</th>
                                            <th className="px-4 py-3 text-left">Zoning</th>
                                            <th className="px-4 py-3 text-left">Listing contact</th>
                                            <th className="px-4 py-3 text-left">Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {s.candidates.map((c) => {
                                            const [label, tone] = USE[c.use_status] || USE.unverified;
                                            return (
                                                <tr key={c.candidate_id} className="align-top">
                                                    <td className="px-4 py-3">
                                                        <p className="font-semibold text-slate-800">{c.address}</p>
                                                        <p className="text-xs text-slate-500">{c.municipality}</p>
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-700">{c.size_sf || '—'}</td>
                                                    <td className="px-4 py-3 text-slate-700">{c.rate || '—'}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>{label}</span>
                                                        {c.zoning_district && <p className="mt-1 text-xs text-slate-500">District {c.zoning_district}</p>}
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-700">
                                                        {c.listing_agent || '—'}
                                                        {c.listing_brokerage && <span className="block text-xs text-slate-500">{c.listing_brokerage}</span>}
                                                        {c.agent_phone && (
                                                            <a className="block text-xs text-indigo-600 hover:underline" href={`tel:${c.agent_phone.replace(/[^\d+]/g, '')}`}>
                                                                {c.agent_phone}
                                                            </a>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-xs text-slate-600">{c.notes}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}

            <p className="text-xs text-slate-500">
                Sizes, rates and availability come from listing services and can change without notice. Zoning
                shown is your broker's reading of the municipal code; a written zoning verification from the
                village is the authority before any lease or purchase is signed.
            </p>
        </div>
    );
};

export default PortalSiteSearchPage;
